import React, { useState } from "react";
import { Plus, Phone, Mail, Edit2, Trash2, X, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

var pipelineStages = [
  { id: "lead", label: "Lead Nuevo", color: "bg-blue-500", lightColor: "bg-blue-50 border-blue-200" },
  { id: "contact", label: "En Contacto", color: "bg-yellow-500", lightColor: "bg-yellow-50 border-yellow-200" },
  { id: "options", label: "Opciones Enviadas", color: "bg-purple-500", lightColor: "bg-purple-50 border-purple-200" },
  { id: "reservation", label: "Reserva", color: "bg-orange-500", lightColor: "bg-orange-50 border-orange-200" },
  { id: "importing", label: "En Importación", color: "bg-cyan-500", lightColor: "bg-cyan-50 border-cyan-200" },
  { id: "delivered", label: "Entregado", color: "bg-green-500", lightColor: "bg-green-50 border-green-200" },
];

var emptyClient = {
  name: "",
  phone: "",
  email: "",
  budget: "",
  interest: "",
  status: "lead",
  notes: ""
};

function CRMPage({ clients, onAddClient, onUpdateClient, onDeleteClient }) {
  var _useState = useState(false);
  var showModal = _useState[0];
  var setShowModal = _useState[1];

  var _useState2 = useState(null);
  var editingClient = _useState2[0];
  var setEditingClient = _useState2[1];

  var _useState3 = useState(emptyClient);
  var formData = _useState3[0];
  var setFormData = _useState3[1];

  var _useState4 = useState("");
  var searchTerm = _useState4[0];
  var setSearchTerm = _useState4[1];

  function openNewClient() {
    setEditingClient(null);
    setFormData(emptyClient);
    setShowModal(true);
  }

  function openEditClient(client) {
    setEditingClient(client);
    setFormData(client);
    setShowModal(true);
  }

  function handleSave() {
    if (!formData.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (editingClient) {
      onUpdateClient(editingClient.id, formData);
      toast.success("Cliente actualizado");
    } else {
      onAddClient(formData);
      toast.success("Cliente añadido");
    }
    setShowModal(false);
  }

  function handleDelete(id) {
    if (window.confirm("¿Eliminar este cliente?")) {
      onDeleteClient(id);
      toast.success("Cliente eliminado");
    }
  }

  function moveClient(clientId, newStatus) {
    onUpdateClient(clientId, { status: newStatus });
    toast.success("Cliente movido a " + pipelineStages.find(function(s) { return s.id === newStatus; }).label);
  }

  // Filter clients by search
  var filteredClients = clients.filter(function(c) {
    if (!searchTerm) return true;
    var term = searchTerm.toLowerCase();
    return c.name.toLowerCase().includes(term) || c.interest.toLowerCase().includes(term);
  });

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-slate-50" data-testid="crm-page">
      {/* Header */}
      <div className="p-6 bg-white border-b">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">CRM Comercial</h1>
            <p className="text-slate-500">Gestión de clientes y pipeline de ventas</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                placeholder="Buscar cliente..." 
                value={searchTerm}
                onChange={function(e) { setSearchTerm(e.target.value); }}
                className="pl-10 w-64"
                data-testid="search-input"
              />
            </div>
            <Button onClick={openNewClient} className="btn-primary gap-2" data-testid="add-client-btn">
              <Plus size={18} /> Nuevo Cliente
            </Button>
          </div>
        </div>
      </div>

      {/* Pipeline */}
      <div className="flex-1 overflow-x-auto p-6">
        <div className="flex gap-4 h-full min-w-max">
          {pipelineStages.map(function(stage) {
            var stageClients = filteredClients.filter(function(c) { return c.status === stage.id; });
            return (
              <div key={stage.id} className="w-72 flex flex-col" data-testid={"stage-" + stage.id}>
                {/* Stage Header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className={"w-3 h-3 rounded-full " + stage.color}></div>
                  <h3 className="font-semibold text-slate-700">{stage.label}</h3>
                  <span className="ml-auto bg-slate-200 text-slate-600 text-xs font-bold px-2 py-0.5 rounded-full">
                    {stageClients.length}
                  </span>
                </div>

                {/* Cards */}
                <div className={"flex-1 overflow-y-auto rounded-lg border p-2 space-y-2 " + stage.lightColor}>
                  {stageClients.map(function(client) {
                    return (
                      <div 
                        key={client.id} 
                        className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow"
                        data-testid={"client-card-" + client.id}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-slate-800">{client.name}</h4>
                          <div className="flex gap-1">
                            <button onClick={function() { openEditClient(client); }} className="p-1 text-slate-400 hover:text-blue-600">
                              <Edit2 size={14} />
                            </button>
                            <button onClick={function() { handleDelete(client.id); }} className="p-1 text-slate-400 hover:text-red-600">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-blue-600 font-medium mb-1">{client.interest}</p>
                        <p className="text-xs text-slate-500 mb-2">{client.budget}</p>
                        {client.phone && (
                          <div className="flex items-center gap-1 text-xs text-slate-400 mb-2">
                            <Phone size={12} /> {client.phone}
                          </div>
                        )}
                        {client.notes && (
                          <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded mt-2 line-clamp-2">{client.notes}</p>
                        )}
                        {/* Move buttons */}
                        <div className="flex gap-1 mt-3 pt-2 border-t">
                          {pipelineStages.map(function(s, idx) {
                            if (s.id === stage.id) return null;
                            var stageIdx = pipelineStages.findIndex(function(x) { return x.id === stage.id; });
                            // Only show adjacent stages
                            if (Math.abs(idx - stageIdx) > 1) return null;
                            return (
                              <button 
                                key={s.id}
                                onClick={function() { moveClient(client.id, s.id); }}
                                className={"text-xs px-2 py-1 rounded border hover:bg-slate-50 " + (idx > stageIdx ? "text-green-600 border-green-200" : "text-slate-500 border-slate-200")}
                              >
                                {idx > stageIdx ? "→ " : "← "}{s.label.split(" ")[0]}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {stageClients.length === 0 && (
                    <p className="text-center text-slate-400 text-sm py-8">Sin clientes</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Client Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg" data-testid="client-modal">
          <DialogHeader>
            <DialogTitle>{editingClient ? "Editar Cliente" : "Nuevo Cliente"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input 
                value={formData.name} 
                onChange={function(e) { setFormData({ ...formData, name: e.target.value }); }}
                placeholder="Nombre completo"
                className="mt-1"
                data-testid="input-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input 
                  value={formData.phone} 
                  onChange={function(e) { setFormData({ ...formData, phone: e.target.value }); }}
                  placeholder="+34 600 000 000"
                  className="mt-1"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <Label>Presupuesto</Label>
                <Input 
                  value={formData.budget} 
                  onChange={function(e) { setFormData({ ...formData, budget: e.target.value }); }}
                  placeholder="40.000-50.000€"
                  className="mt-1"
                  data-testid="input-budget"
                />
              </div>
            </div>
            <div>
              <Label>Vehículo de interés</Label>
              <Input 
                value={formData.interest} 
                onChange={function(e) { setFormData({ ...formData, interest: e.target.value }); }}
                placeholder="BMW Serie 3, Audi A4..."
                className="mt-1"
                data-testid="input-interest"
              />
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={formData.status} onValueChange={function(v) { setFormData({ ...formData, status: v }); }}>
                <SelectTrigger className="mt-1" data-testid="select-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pipelineStages.map(function(s) {
                    return <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notas internas</Label>
              <Textarea 
                value={formData.notes} 
                onChange={function(e) { setFormData({ ...formData, notes: e.target.value }); }}
                placeholder="Notas sobre el cliente..."
                className="mt-1"
                data-testid="input-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowModal(false); }}>Cancelar</Button>
            <Button onClick={handleSave} className="btn-primary" data-testid="save-client-btn">
              {editingClient ? "Guardar" : "Añadir Cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CRMPage;
